import { useEffect, useRef, useState } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { useInViewport } from 'ahooks';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { useDate } from '~/libs/hooks/useDate';
import { useParams } from '~/router';

import { ReportListTimelapseModel } from '../../libs/types';

export interface ReportTimelapsesItemProps {
  data: ReportListTimelapseModel;
}

export const ReportTimelapsesItem = (props: ReportTimelapsesItemProps) => {
  const { data } = props;
  const { formatDate } = useDate();
  const { projectId } = useParams('/projects/:projectId/reports');

  return (
    <ItemWrap>
      <Box color="#2C384A" fontSize="14px" mb="8px" px="1rem">
        {formatDate(data._id)}
      </Box>

      {data.timelapse.map((item) => {
        return (
          <VideoPlayer
            key={item._id}
            src={item.url}
            poster={
              item.thumbnailUrl ||
              `https://video.hausplanner.com/video/${projectId}/thumb/${dayjs(item.date).format(
                'YYYY-MM-DD',
              )}.jpg`
            }
            cameraName={item.setting?.cameraName}
          />
        );
      })}
    </ItemWrap>
  );
};

interface VideoPlayerProps {
  src: string;
  poster?: string;
  cameraName?: string | null;
}
const VideoPlayer = (props: VideoPlayerProps) => {
  const { src, poster, cameraName } = props;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [inViewport] = useInViewport(videoRef);
  const [_isPlaying, setIsPlaying] = useState(false);
  const [isPlayedBefore, setIsPlayedBefore] = useState(false);
  const [_isFullScreen, setIsFullScreen] = useState(false);

  const handleClickPlay = () => {
    videoRef.current?.play();
    setIsPlayedBefore(true);
  };

  useEffect(() => {
    if (inViewport) return;
    videoRef.current?.pause();
  }, [inViewport]);

  useEffect(() => {
    const onFullScreenChange = () => {
      if (document.fullscreenElement === videoRef.current) {
        setIsFullScreen(true);
      } else {
        setIsFullScreen(false);
      }
    };
    document.addEventListener('fullscreenchange', onFullScreenChange);
    document.addEventListener('mozfullscreenchange', onFullScreenChange);
    document.addEventListener('webkitfullscreenchange', onFullScreenChange);
    document.addEventListener('MSFullscreenChange', onFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', onFullScreenChange);
      document.removeEventListener('mozfullscreenchange', onFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullScreenChange);
      document.removeEventListener('MSFullscreenChange', onFullScreenChange);
    };
  }, []);

  return (
    <Box>
      <TimelapseBlock>
        <Video
          muted
          playsInline
          webkit-playsinline="true"
          controls={isPlayedBefore}
          preload="none"
          ref={videoRef}
          poster={poster}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        >
          <source src={src} type="video/mp4" />
        </Video>
        {!isPlayedBefore && (
          <VideoCover
            onClick={handleClickPlay}
            style={{
              backgroundImage: poster ? (isPlayedBefore ? 'none' : `url(${poster})`) : 'none',
            }}
          >
            <svg
              width="60"
              height="60"
              viewBox="0 0 60 60"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="30" cy="30" r="30" fill="white" />
              <path
                d="M43.8162 29.5773L23.5933 41.2869L23.5933 17.8676L43.8162 29.5773Z"
                fill="#262626"
              />
            </svg>
          </VideoCover>
        )}
      </TimelapseBlock>
      <Box py="4px" px="1rem" color="#7F8892" fontSize="14px" textAlign="right">
        {cameraName}
      </Box>
    </Box>
  );
};

const ItemWrap = styled.div`
  padding-bottom: 12px;
  border-bottom: 1px solid #eeeeee;
  margin-bottom: 16px;
`;

const TimelapseBlock = styled.div`
  position: relative;
  height: 0;
  padding-bottom: 56.25%;
`;

const Video = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const VideoCover = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  background-position: center;
  background-size: cover;
`;
